import React, { useState } from 'react';
import {
  Button,
  Grid,
  IconButton,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowDropDown, ArrowDropUp, Help } from '@mui/icons-material';
import { Loading } from '../../components';
import { CustomIcon, RootineSelectField } from '../../atoms';
import { format } from 'date-fns';
import { DataType } from '../../types';
import FilterIcon from '@mui/icons-material/FilterAlt';

export type ColumnSort = {
  field: string;
  direction: 'asc' | 'desc' | string;
};

export interface TableAction {
  header: string;
  onClick: (i: number, data: any | undefined) => void;
  element?: string;
  icon?: string;
  iconColor?: string;
  selectOptions?: { [name: string]: string }[];
  value?: string | number;
  order?: number;
}

export interface Column {
  name: string;
  label: string;
  editable?: boolean;
  type?: DataType;
  sorted?: 'asc' | 'desc';
  helpText?: string;
  fieldOptions?: string[];
}

export interface TableProps {
  columns: Column[];
  rows: { [name: string]: string }[] | any;
  onFilter?: (field: string, value: string) => void;
  maxHeight?: number;
  actions?: TableAction[];
  sortData?: (data: ColumnSort) => void;
  emptyMessage?: JSX.Element;
}

export const AdminTable = (props: TableProps): JSX.Element => {
  const prefixActions =
    props.actions &&
    Object.values(props.actions).filter(
      (action) => action.order && action.order < 0
    );
  const suffixActions =
    props.actions &&
    Object.values(props.actions).filter((action) => !action.order);

  const renderAction = (action: TableAction, idx: number): JSX.Element => {
    return (
      <CustomIcon
        name={action.icon || 'downArrow'}
        onClick={() => action.onClick(idx, props.rows[idx])}
        sx={{ cursor: 'pointer' }}
        fontSize="medium"
        htmlColor={action.iconColor}
      />
    );
  };

  const renderElement = (action: TableAction, idx: number): JSX.Element => {
    if (action.element === 'select') {
      let opt: { [name: string]: string } = {};
      if (action.value && action.selectOptions) {
        const filtVals = action.selectOptions.filter(
          (option) =>
            action.value && option.name === props.rows[idx][action.value]
        );
        opt = filtVals[0];
      }
      return (
        <RootineSelectField
          name={`field ${idx}`}
          options={action.selectOptions}
          onChange={(res: any) => action.onClick(idx, res)}
          value={opt}
        />
      );
    } else {
      console.error('Unsupported element render failed: ', action.element);
      return <></>;
    }
  };

  const handleHeaderClick = (
    coldata: Column | undefined,
    direction: 'asc' | 'desc'
  ) => {
    if (coldata) {
      if (props.sortData) {
        const d: ColumnSort = {
          field: coldata.name,
          direction: direction,
        };
        props.sortData(d);
      }
    }
  };

  const renderHeader = (
    key: string,
    column: string,
    alignment: 'left' | 'center' | 'right',
    sortable: boolean,
    coldata?: Column
  ) => {
    const [showFilterInput, setShowFilterInput] = useState<boolean>(false);
    const [filterValue, setFilterValue] = useState<string>('');
    return (
      <TableCell
        key={key}
        sx={{
          cursor: sortable ? 'pointer' : 'inherit',
          width: 'auto',
          whiteSpace: 'nowrap',
        }}
      >
        <Grid>
          {coldata && (
            <ArrowDropUp
              sx={{ position: 'relative', top: 6 }}
              onClick={() => handleHeaderClick(coldata, 'asc')}
            />
          )}
          {coldata && (
            <ArrowDropDown
              sx={{ position: 'relative', top: 6 }}
              onClick={() => handleHeaderClick(coldata, 'desc')}
            />
          )}
          {coldata?.helpText && (
            <Tooltip title={coldata.helpText}>
              <IconButton>
                <Help sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          <Typography
            color="text.secondary"
            sx={{ fontSize: 16, fontWeight: 500 }}
            align={alignment}
          >
            {column}
            {key.startsWith('data') && (
              <FilterIcon
                sx={{ fontSize: 12, ml: 1 }}
                onClick={() => setShowFilterInput(!showFilterInput)}
              />
            )}
          </Typography>
        </Grid>

        {showFilterInput && (
          <Grid container>
            <Grid item xs={12}>
              <TextField
                variant="outlined"
                size="small"
                onChange={(e) => setFilterValue(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                size="small"
                onClick={() =>
                  props.onFilter &&
                  props.onFilter(coldata?.name || column, filterValue)
                }
              >
                Filter
              </Button>
            </Grid>
          </Grid>
        )}
      </TableCell>
    );
  };

  const renderField = (field: string, columnIdx: number): JSX.Element => {
    // TODO: handle double click field to do an inline edit
    // check if field is editable as well
    const coltype = props.columns[columnIdx];
    if (coltype.type && field) {
      switch (coltype?.type) {
        case DataType.Date:
          return (
            <Typography onDoubleClick={() => console.log('db click')}>
              {format(Date.parse(field), 'MM/DD/YYYY HH:mm:ss')}
            </Typography>
          );
        default:
          return (
            <Typography onDoubleClick={() => console.log('db click')}>
              {field}
            </Typography>
          );
      }
    } else {
      return (
        <Typography onDoubleClick={() => console.log('db vlick')}>
          {field}
        </Typography>
      );
    }
  };

  return (
    <>
      {props.rows && (
        <>
          <TableContainer
            sx={{
              position: 'relative',
              top: -20,
              overflow: 'scroll-y',
              maxHeight: props.maxHeight,
              '&::-webkit-scrollbar': { width: 5 },
              '&::-webkit-scrollbar-track': { background: '#F8F8F8' },
              '&::-webkit-scrollbar-thumb': {
                background: '#888',
                borderRadius: 5,
              },
            }}
          >
            <Table aria-label="Data table" sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  {prefixActions &&
                    Object.values(prefixActions).map(
                      (action: TableAction, i: number) =>
                        renderHeader(
                          `action-${i}`,
                          action.header,
                          'left',
                          false
                        )
                    )}
                  {props.columns.map((column: Column, i: number) =>
                    renderHeader(
                      `data-${i}`,
                      column.label,
                      'left',
                      true,
                      column
                    )
                  )}
                  {suffixActions &&
                    Object.values(suffixActions).map(
                      (action: TableAction, i: number) =>
                        renderHeader(
                          `action-${i}`,
                          action.header,
                          'center',
                          false
                        )
                    )}
                </TableRow>
              </TableHead>
              <TableBody
                sx={{
                  '& .MuiTableRow-root:hover': {
                    background:
                      'linear-gradient(146deg, rgba(249, 225, 255, 0.4) 17%, rgba(225, 244, 255, 0.5))',
                  },
                }}
              >
                {props.rows &&
                  props.rows.map(
                    (row: { [name: string]: string }, i: number) => {
                      return (
                        <TableRow
                          key={i}
                          hover
                          sx={{
                            '&:last-child td, &:last-child th': { border: 0 },
                          }}
                        >
                          {prefixActions &&
                            Object.values(prefixActions).map(
                              (action: TableAction, idx: number) => {
                                return (
                                  <TableCell
                                    key={`${idx}-row-${action.header}`}
                                    component="th"
                                    scope="row"
                                  >
                                    {action.icon && renderAction(action, i)}
                                    {action.element && renderElement(action, i)}
                                  </TableCell>
                                );
                              }
                            )}
                          {props.columns.map((field: Column, id: number) => {
                            return (
                              <TableCell
                                key={`${id}-` + field}
                                scope="row"
                                sx={{
                                  // whiteSpace: "normal",
                                  maxWidth: 200,
                                  overflowWrap: 'break-word',
                                }}
                              >
                                {renderField(row[field.name], id)}
                              </TableCell>
                            );
                          })}
                          {suffixActions &&
                            Object.values(suffixActions).map(
                              (action: TableAction, idx: number) => {
                                return (
                                  <TableCell
                                    key={`${idx}-row-${action.header}`}
                                    component="th"
                                    scope="row"
                                    align="center"
                                  >
                                    {action.icon && renderAction(action, i)}

                                    {action.element && renderElement(action, i)}
                                  </TableCell>
                                );
                              }
                            )}
                        </TableRow>
                      );
                    }
                  )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
      {!props.rows && <Loading />}
      {props.rows && props.rows.length === 0 && (
        <Grid container justifyContent="center" alignItems="center">
          {props.emptyMessage}
        </Grid>
      )}
    </>
  );
};
